// Menaje y sobrantes (reportes + catálogo). El registro de envío/devolución se
// hace en el Despacho; aquí se ve el ACTIVO que no volvió (saldo de menaje por
// cliente) y el análisis de sobrantes. Catering → Logística → Menaje y sobrantes.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Utensils, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, num } from '@/features/inventario/shared';
import { catRpc, money, SERVICIO_LABEL } from './shared';

interface SaldoDet { menaje: string; saldo: number; valor: number }
interface Saldo { contrato_id: string; cliente: string; enviado: number; devuelto: number; saldo: number; valor_pendiente: number; ultimo_movimiento: string; detalle: SaldoDet[] }
interface SobranteRow { cliente?: string; servicio?: string; despachadas: number; devueltas: number; pct: number | null }
interface Sobrantes { por_cliente: SobranteRow[]; por_servicio: SobranteRow[] }
interface MenajeCat { id: string; codigo: string | null; nombre: string; valor_unitario: number; es_activo: boolean }

const isoDesde = (): string => { const d = new Date(); d.setDate(d.getDate() - 30); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const isoHoy = (): string => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

export function MenajeCatering() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'saldo' | 'sobrantes' | 'catalogo'>('saldo');
  const [desde, setDesde] = useState(isoDesde());
  const [hasta, setHasta] = useState(isoHoy());

  const { data: saldo = [] } = useQuery<Saldo[]>({ queryKey: ['cat-menaje-saldo'], queryFn: () => catRpc<Saldo[]>('cat_menaje_saldo'), enabled: tab === 'saldo' });
  const { data: sobr } = useQuery<Sobrantes>({ queryKey: ['cat-sobrantes', desde, hasta], queryFn: () => catRpc<Sobrantes>('cat_sobrantes_resumen', { _desde: desde, _hasta: hasta }), enabled: tab === 'sobrantes' });
  const { data: cat = [] } = useQuery<MenajeCat[]>({ queryKey: ['cat-menaje-cat'], queryFn: () => catRpc<MenajeCat[]>('cat_menaje_listar'), enabled: tab === 'catalogo' });

  const [edit, setEdit] = useState<{ id?: string; nombre: string; valor_unitario: string } | null>(null);
  const guardar = useMutation({
    mutationFn: (m: { id?: string; nombre: string; valor_unitario: string }) =>
      catRpc('cat_menaje_guardar', { _data: { nombre: m.nombre, valor_unitario: m.valor_unitario || '0' }, _id: m.id ?? null }),
    onSuccess: () => { toast.success('Menaje guardado'); setEdit(null); void qc.invalidateQueries({ queryKey: ['cat-menaje-cat'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const totalPend = saldo.reduce((s, x) => s + Number(x.valor_pendiente || 0), 0);

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap gap-1">
        {([['saldo', 'Saldo por cliente'], ['sobrantes', 'Sobrantes'], ['catalogo', 'Catálogo']] as const).map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className={`rounded-full px-3 py-1 text-sm ${tab === k ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>{label}</button>
        ))}
      </div>

      {tab === 'saldo' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Boxes className="h-4 w-4" /> Menaje sin devolver</CardTitle>
            <p className="pt-1 text-xs text-muted-foreground">Contenedores retornables enviados que aún no volvieron, por cliente. Total en riesgo: <strong>{money(totalPend, 2)}</strong></p>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-1 pr-2">Cliente</th><th className="py-1 pr-2 text-right">Enviado</th><th className="py-1 pr-2 text-right">Devuelto</th>
                <th className="py-1 pr-2 text-right">Saldo</th><th className="py-1 pr-2 text-right">Valor</th><th className="py-1 pr-2">Detalle</th>
              </tr></thead>
              <tbody>
                {saldo.map((x) => (
                  <tr key={x.contrato_id} className="border-b last:border-0">
                    <td className="py-1.5 pr-2 font-medium">{x.cliente}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{num(x.enviado, 0)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{num(x.devuelto, 0)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono text-amber-600">{num(x.saldo, 0)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono text-amber-600">{money(x.valor_pendiente, 2)}</td>
                    <td className="py-1.5 pr-2 text-xs text-muted-foreground">{x.detalle.map((d) => `${d.menaje}: ${d.saldo}`).join(' · ')}</td>
                  </tr>
                ))}
                {!saldo.length && <tr><td colSpan={6} className="py-3 text-sm text-muted-foreground">Sin saldos pendientes: todo el menaje volvió. 👍</td></tr>}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {tab === 'sobrantes' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Utensils className="h-4 w-4" /> Sobrantes (raciones que volvieron sin servir)</CardTitle>
            <div className="flex flex-wrap items-end gap-2 pt-1">
              <div><label className="block text-xs text-muted-foreground">Desde</label><input type="date" className={`${fieldCls} w-40`} value={desde} onChange={(e) => setDesde(e.target.value)} /></div>
              <div><label className="block text-xs text-muted-foreground">Hasta</label><input type="date" className={`${fieldCls} w-40`} value={hasta} onChange={(e) => setHasta(e.target.value)} /></div>
            </div>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="pb-1 text-sm font-medium">Por cliente</p>
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-1 pr-2">Cliente</th><th className="py-1 pr-2 text-right">Despachadas</th><th className="py-1 pr-2 text-right">Sobraron</th><th className="py-1 pr-2 text-right">%</th></tr></thead>
                <tbody>
                  {(sobr?.por_cliente ?? []).map((y, i) => (
                    <tr key={i} className="border-b last:border-0"><td className="py-1 pr-2">{y.cliente}</td><td className="py-1 pr-2 text-right font-mono">{num(y.despachadas, 0)}</td><td className="py-1 pr-2 text-right font-mono">{num(y.devueltas, 0)}</td><td className="py-1 pr-2 text-right font-mono">{y.pct != null ? `${y.pct}%` : '—'}</td></tr>
                  ))}
                  {!sobr?.por_cliente?.length && <tr><td colSpan={4} className="py-2 text-sm text-muted-foreground">Sin sobrantes en el período.</td></tr>}
                </tbody>
              </table>
            </div>
            <div>
              <p className="pb-1 text-sm font-medium">Por servicio</p>
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-1 pr-2">Servicio</th><th className="py-1 pr-2 text-right">Despachadas</th><th className="py-1 pr-2 text-right">Sobraron</th><th className="py-1 pr-2 text-right">%</th></tr></thead>
                <tbody>
                  {(sobr?.por_servicio ?? []).map((z, i) => (
                    <tr key={i} className="border-b last:border-0"><td className="py-1 pr-2">{SERVICIO_LABEL[z.servicio ?? ''] ?? z.servicio}</td><td className="py-1 pr-2 text-right font-mono">{num(z.despachadas, 0)}</td><td className="py-1 pr-2 text-right font-mono">{num(z.devueltas, 0)}</td><td className="py-1 pr-2 text-right font-mono">{z.pct != null ? `${z.pct}%` : '—'}</td></tr>
                  ))}
                  {!sobr?.por_servicio?.length && <tr><td colSpan={4} className="py-2 text-sm text-muted-foreground">Sin sobrantes en el período.</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {tab === 'catalogo' && (
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">Tipos de menaje retornable</CardTitle>
            <Button size="sm" variant="outline" onClick={() => setEdit({ nombre: '', valor_unitario: '' })}><Plus className="mr-1 h-3.5 w-3.5" /> Nuevo</Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {edit && (
              <div className="flex flex-wrap items-end gap-2 rounded-md border p-2">
                <div><label className="block text-xs text-muted-foreground">Nombre *</label><input className={`${fieldCls} w-52`} placeholder="Termo 40L" value={edit.nombre} onChange={(e) => setEdit({ ...edit, nombre: e.target.value })} /></div>
                <div><label className="block text-xs text-muted-foreground">Valor unitario ($)</label><input className={`${fieldCls} w-28`} type="number" step="0.01" value={edit.valor_unitario} onChange={(e) => setEdit({ ...edit, valor_unitario: e.target.value })} /></div>
                <Button size="sm" disabled={!edit.nombre || guardar.isPending} onClick={() => guardar.mutate(edit)}>Guardar</Button>
                <Button size="sm" variant="ghost" onClick={() => setEdit(null)}>Cancelar</Button>
              </div>
            )}
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-1 pr-2">Código</th><th className="py-1 pr-2">Nombre</th><th className="py-1 pr-2 text-right">Valor</th><th /></tr></thead>
              <tbody>
                {cat.map((m) => (
                  <tr key={m.id} className={`border-b last:border-0 ${!m.es_activo ? 'opacity-50' : ''}`}>
                    <td className="py-1.5 pr-2 font-mono text-xs">{m.codigo}</td>
                    <td className="py-1.5 pr-2">{m.nombre}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{money(m.valor_unitario, 2)}</td>
                    <td className="py-1.5 text-right"><button className="text-xs text-primary underline" onClick={() => setEdit({ id: m.id, nombre: m.nombre, valor_unitario: String(m.valor_unitario) })}>editar</button></td>
                  </tr>
                ))}
                {!cat.length && <tr><td colSpan={4} className="py-2 text-sm text-muted-foreground">Sin tipos de menaje. Crea el primero (termos, bandejas, cajas isotérmicas).</td></tr>}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
